import NodeStatus from 'components/NodeStatus/NodeStatus';
import { USGSMeasureNormalizer } from 'features/integrations/features/ConnectedDevices/features/DeviceGraph/utils/measurementUtils';
import { exampleUSGSDevice } from 'features/integrations/features/ConnectedDevices/types/USGSMONITORINGTYPE';
import {
  usgsGetAllTimeSeriesWithValues,
  usgsGetMostRecentValueForParam,
  usgsGetMostRecentValues,
} from 'features/integrations/features/ConnectedDevices/utils/usgsGeneralUtils';
import { useEffect, useState } from 'react';
import { fetchAwareImageObjectUrl } from 'utils/awareImageUtils';
import { extractDateTimeFromUrl } from 'utils/dateUtils';

export default function DeviceMapDataView({ device }) {
  let deviceType = device.system_type + '_' + device.device_type;

  switch (deviceType.toLowerCase()) {
    case 'aware_flood':
      return <RenderAwareMapDeviceData device={device} />;
    case 'usgs_monitoring station':
      return <RenderUSGSMapDeviceData device={device} />;
    default:
      return <RenderRescAlertMapDeviceData device={device} />;
  }
}

function RenderRescAlertMapDeviceData({ device }) {
  return (
    <>
      <div className="d-flex flex-column justify-content-center align-content-center gap-2 mb-2">
        <h2>
          {(device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
            device.alias}
        </h2>
        <div className="d-flex flex-row justify-content-center align-content-center align-items-center gap-2">
          <b>Status:</b> <NodeStatus device={device} />
        </div>
        <b>
          {device?.data?.[0]?.date_created
            ? new Date(device.data[0].date_created).toLocaleString()
            : 'No timestamp'}
        </b>
        <div>
          <b>Comments:</b> {device?.data?.[0]?.comments || <i>No comments available.</i>}
        </div>
      </div>
    </>
  );
}

// SYSTEM SPECIFIC DEVICE RENDERING ON DEVICES MAP
function RenderAwareMapDeviceData({ device }) {
  const deviceData = device?.data?.[0];
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      if (!deviceData?.img_link_url) {
        setImageUrl(null);
        return;
      }

      objectUrl = await fetchAwareImageObjectUrl(deviceData.img_link_url);

      if (!isMounted) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        return;
      }

      setImageUrl(objectUrl);
    };

    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [deviceData?.img_link_url]);

  return (
    <>
      <h2>
        {(device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
          device.alias}
      </h2>
      {device?.data[0] && (
        <div>
          {new Date(device?.data[0].ts).toLocaleDateString()},
          {new Date(device?.data[0].ts).toLocaleTimeString()}
        </div>
      )}
      <div className="d-flex flex-row justify-content-center align-content-center align-items-center gap-2 mb-2 mt-1">
        <b>Status:</b> <NodeStatus device={device} />
      </div>
      <div className="d-flex flex-row mb-2 justify-content-between gap-3 text-nowrap">
        <div className="d-flex flex-column text-start text-nowrap gap-1">
          <div className="d-flex flex-row flex-nowrap">
            <div
              style={{
                width: 25,
                height: 10,
                background: '#FFFF00',
                marginRight: 5,
                marginTop: 2,
              }}
            ></div>
            Actual Depth:{' '}
            {device?.data[0].depth1 +
              device?.water_level_adj +
              ' in/' +
              ((device?.data[0].depth1 + device?.water_level_adj) / 12).toFixed(3) +
              ' ft'}
          </div>
          <div className="d-flex flex-row flex-nowrap">
            <div
              style={{
                width: 25,
                height: 10,
                background: '#0075ff',
                marginRight: 5,
                marginTop: 2,
              }}
            ></div>
            Depth:{' '}
            {device?.data[0].depth1 + ' in/' + (device?.data[0].depth1 / 12).toFixed(3) + ' ft'}
          </div>
          <div className="d-flex flex-row flex-nowrap">
            <div
              style={{
                width: 25,
                height: 10,
                background: '#62f671',
                marginRight: 5,
                marginTop: 2,
              }}
            ></div>
            Temperature:{' '}
            {device?.data[0].h2oTemp +
              '°C/' +
              ((device?.data[0].h2oTemp * 9) / 5 + 32).toFixed(2) +
              ' °F'}
          </div>
          <div className="d-flex flex-row flex-nowrap">
            <div
              style={{
                width: 25,
                height: 10,
                background: '#7f00ff',
                marginRight: 5,
                marginTop: 2,
              }}
            ></div>
            Pressure:
            {device?.data[0].baro +
              ' mBar/' +
              (device?.data[0].baro * 0.02952998).toFixed(3) +
              ' inHg'}
          </div>
          <div className="d-flex flex-row flex-nowrap">
            <div
              style={{
                width: 25,
                height: 10,
                background: '#afb943',
                marginRight: 5,
                marginTop: 2,
              }}
            ></div>
            Battery: {device?.data[0].battery}V{' '}
          </div>
          <div className="d-flex flex-row flex-nowrap">
            <div
              style={{
                width: 25,
                height: 10,
                background: 'red',
                marginRight: 5,
                marginTop: 2,
              }}
            ></div>
            Alert Indicated:{' '}
            {device?.data[0].ffi1 || device?.data[0].drInd || device?.data[0].depthInd
              ? 'True'
              : 'False'}
          </div>
        </div>
        {imageUrl && (
          <div className="d-flex flex-column">
            <img src={imageUrl} alt="Device" style={{ width: '200px', height: '150px' }} />
            <div>
              Image Timestamp:{' \n'}
              {extractDateTimeFromUrl(device?.data[0].img_link_url)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function RenderUSGSMapDeviceData({ device }) {
  const mostRecent = usgsGetMostRecentValues(device) || {};

  const latest = Object.entries(mostRecent).map(([timeSeriesId, data]) => {
    const isUnavail = data?.value === null;
    const normalized = isUnavail ? null : USGSMeasureNormalizer(data?.value, data?.unit_of_measure);
    return {
      timeseries: timeSeriesId,
      parameter_name: data?.parameterName ?? null,
      value: isUnavail ? 'UNAVAILABLE' : (data?.value ?? null),
      unit: normalized?.unit ?? data?.unit_of_measure ?? null,
      alt_value: normalized?.alt_value ?? null,
      alt_unit: normalized?.alt_unit ?? null,
      validValue: normalized != null,
      showAlt: normalized?.alt_value != null,
      ts: data?.ts ?? null,
      time: data?.ts ?? null,
      normalizedValue: normalized?.value ?? data?.value ?? null,
    };
  });
  const monitoringId = device?.monitoring_location_id || device?.name || 'N/A';

  return (
    <div className="mb-1">
      <div className="fw-bold">
        {/* Device Alias or Monitoring Location Name */}
        {device?.alias && String(device.alias).trim() !== ''
          ? device.alias
          : device.monitoring_location_name}
      </div>
      <div>Monitoring Location ID: {monitoringId}</div>
      <div className="d-flex flex-column gap-1 p-2 border border-1 border-dark">
        {latest.length ? (
          latest.map((data) => {
            return (
              <div key={`${data.timeseries}`}>
                {/* Parameter Name and Value */}
                <div className="fw-bold">{data.parameter_name}</div>
                <div>
                  {String(data?.value)}
                  {!!data.validValue && data.unit ? ' ' : null}
                  {!!data.validValue && data.unit ? data.unit : null}
                  {/* Only render alt Value if available */}
                  {!!data.showAlt ? (
                    <>
                      {' / '}
                      {data.alt_value}
                      {data.alt_unit ? ' ' : null}
                      {data.alt_unit}
                    </>
                  ) : null}
                </div>
                <div>
                  {/* Date Time */}
                  {(() => {
                    const raw = data?.time ?? data?.ts ?? null;
                    if (raw == null) return 'No timestamp';

                    let parsed;
                    if (typeof raw === 'number' || /^\d+$/.test(String(raw))) {
                      parsed = new Date(Number(raw));
                    } else {
                      parsed = new Date(String(raw));
                    }
                    if (Number.isNaN(parsed.getTime())) return 'No timestamp';

                    return new Intl.DateTimeFormat('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      timeZoneName: 'short',
                    }).format(parsed);
                  })()}
                </div>
              </div>
            );
          })
        ) : (
          <div>No USGS datapoints available.</div>
        )}
      </div>
    </div>
  );
}
